import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Section } from '../shared';

// Tooltip Component
const Tooltip = ({ children, content, stats }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 z-50"
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            transition={{ duration: 0.2 }}
          >
            <div className="bg-dark-deeper border border-neon-orange/30 rounded-xl p-4 shadow-xl min-w-[200px]">
              <div className="text-neon-orange font-bold text-lg mb-1">{stats}</div>
              <div className="text-gray-400 text-sm">{content}</div>
              {/* Arrow */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-[1px]">
                <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-neon-orange/30" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Benefit Pillar Icons
const PillarIcon = ({ type }) => {
  const icons = {
    time: '/Why%20Choose%20Us.webp',
    quality: '/Why%20Choose%20Us%20(2).webp',
    scale: '/Why%20Choose%20Us%20(3).webp',
    cost: '/Why%20Choose%20Us%20(4).webp',
    ease: '/Why%20Choose%20Us%20(5).webp',
  };

  const icon = icons[type] || icons.time;
  if (typeof icon === 'string') {
    return <img src={icon} alt={type} className="w-10 h-10 object-contain" />;
  }
  return icon;
};

// Benefit Pillar Component
const BenefitPillar = ({ benefit, index, isInView }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Tooltip content={benefit.tooltipText} stats={benefit.stats}>
      <motion.div
        className="flex flex-col items-center"
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: index * 0.15 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        {/* Icon */}
        <motion.div
          className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 ${
            isHovered 
              ? 'bg-neon-orange text-white' 
              : 'bg-neon-orange/10 text-neon-orange'
          }`}
          animate={{ 
            scale: isHovered ? 1.1 : 1,
            rotate: isHovered ? [0, -5, 5, 0] : 0
          }}
        >
          <PillarIcon type={benefit.iconType} />
        </motion.div>

        {/* Pillar Bar */}
        <motion.div
          className={`relative w-20 md:w-24 rounded-2xl overflow-hidden transition-all duration-300 ${
            isHovered 
              ? 'shadow-[0_0_30px_rgba(253,128,46,0.4)]' 
              : ''
          }`}
          style={{ height: '280px' }}
          animate={{
            borderColor: isHovered ? 'rgba(253, 128, 46, 0.8)' : 'rgba(253, 128, 46, 0.2)',
          }}
        >
          {/* Background */}
          <div className="absolute inset-0 bg-dark-deeper border-2 border-neon-orange/20 rounded-2xl" />
          
          {/* Glow border on hover */}
          <motion.div
            className="absolute inset-0 rounded-2xl border-2 pointer-events-none"
            animate={{
              borderColor: isHovered ? 'rgba(253, 128, 46, 0.8)' : 'rgba(253, 128, 46, 0)',
              boxShadow: isHovered 
                ? 'inset 0 0 20px rgba(253, 128, 46, 0.2)' 
                : 'inset 0 0 0px rgba(253, 128, 46, 0)'
            }}
          />

          {/* Progress Fill */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-neon-orange via-neon-orange to-neon-orange-light rounded-b-xl"
            initial={{ height: 0 }}
            animate={isInView ? { height: `${benefit.fillPercent}%` } : {}}
            transition={{ 
              duration: 1.5, 
              delay: index * 0.2,
              ease: "easeOut"
            }}
          >
            {/* Shimmer effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-t from-transparent via-white/20 to-transparent"
              animate={{ y: ['-100%', '100%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            />
          </motion.div>

          {/* Percentage label */}
          <motion.div
            className="absolute top-4 left-0 right-0 text-center"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: index * 0.2 + 1 }}
          >
            <span className="text-white font-bold text-lg">{benefit.fillPercent}%</span>
          </motion.div>
        </motion.div>

        {/* Title */}
        <motion.h3
          className="text-white font-heading font-bold text-center mt-4 mb-2 text-sm md:text-base"
          animate={{ color: isHovered ? '#FD802E' : '#ffffff' }}
        >
          {benefit.title}
        </motion.h3>

        {/* Description */}
        <p className="text-gray-400 text-xs md:text-sm text-center max-w-[120px]">
          {benefit.description}
        </p>
      </motion.div>
    </Tooltip>
  );
};

const BenefitsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const benefits = [
    { 
      title: "Save Time", 
      description: "Automate tedious audio tasks",
      iconType: "time",
      fillPercent: 75,
      stats: "Save 50% Time",
      tooltipText: "Users report cutting their editing time in half with our automation tools."
    },
    { 
      title: "Studio Quality", 
      description: "Professional-grade output",
      iconType: "quality",
      fillPercent: 95,
      stats: "98% Quality Score",
      tooltipText: "Achieve broadcast-ready audio quality without expensive studio equipment."
    },
    { 
      title: "Scale Fast", 
      description: "Process thousands of files",
      iconType: "scale",
      fillPercent: 88,
      stats: "10x Faster",
      tooltipText: "Batch process up to 10,000 files simultaneously with parallel processing."
    },
    { 
      title: "Cut Costs", 
      description: "Replace multiple tools",
      iconType: "cost",
      fillPercent: 65,
      stats: "Save $500/mo",
      tooltipText: "Consolidate your audio toolkit and reduce software subscription costs."
    },
    { 
      title: "Easy to Use", 
      description: "No learning curve",
      iconType: "ease",
      fillPercent: 82,
      stats: "5min Setup",
      tooltipText: "Get started in minutes with our intuitive drag-and-drop interface."
    },
  ];

  return (
    <Section id="benefits" className="bg-dark-base relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 7, repeat: Infinity }}
        />
      </div>

      <div className="max-w-6xl mx-auto relative z-10" ref={ref}>
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.span 
            className="text-neon-orange font-semibold text-sm uppercase tracking-wider"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
          >
            Why Choose Us
          </motion.span>
          
          <motion.h2 
            className="text-3xl md:text-4xl font-heading font-bold text-white mt-4 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
          >
            Benefits That{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-orange to-neon-orange-light">
              Transform
            </span>{' '}
            Your Workflow
          </motion.h2>
          
          <motion.p 
            className="text-gray-400 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            Hover over each pillar to see quantifiable results from our users.
          </motion.p>
        </div>

        {/* Benefits Pillars - Flexbox Row */}
        <div className="flex flex-wrap justify-center gap-6 md:gap-8 lg:gap-12">
          {benefits.map((benefit, i) => (
            <BenefitPillar 
              key={i} 
              benefit={benefit} 
              index={i} 
              isInView={isInView}
            />
          ))}
        </div>
      </div>

      {/* Bottom gradient overlay for smooth transition */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-dark-deeper to-transparent pointer-events-none" />
    </Section>
  );
};

export default BenefitsSection;
